import React from 'react';
import { Button } from 'react-bootstrap';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const VatStepNavigation = ({
    currentStep,
    stepOrder,
    onStepChange,
    onCompletePeriod,
    operationInProgress,
    canProceedToNext,
    canGoBack,
    vatPeriod,
    recordVerification
}) => {
    const getCurrentStepIndex = () => {
        return stepOrder.indexOf(currentStep);
    };

    const getNextStep = () => {
        const currentIndex = getCurrentStepIndex();
        if (currentIndex < stepOrder.length - 1) {
            return stepOrder[currentIndex + 1];
        }
        return null;
    };

    const getPreviousStep = () => {
        const currentIndex = getCurrentStepIndex();
        if (currentIndex > 0) {
            return stepOrder[currentIndex - 1];
        }
        return null;
    };

    const handleNext = () => {
        const nextStep = getNextStep();
        if (nextStep && onStepChange) {
            onStepChange(nextStep, 'forward');
        }
    };

    const handlePrevious = () => {
        const previousStep = getPreviousStep();
        if (previousStep && onStepChange) {
            onStepChange(previousStep, 'backward');
        }
    };

    const getNextButtonText = () => {
        const stepTexts = {
            'instructions': 'Fortsätt till granskning',
            'review': 'Godkänn och fortsätt',
            'submission': 'Fortsätt till betalning',
            'payment': 'Slutför och bokför',
            'record': 'Avsluta'
        };
        return stepTexts[currentStep] || 'Nästa steg';
    };

    const isLastStep = () => {
        return getCurrentStepIndex() === stepOrder.length - 1;
    };

    // Hide navigation entirely when verification is created (workflow complete)
    const isWorkflowComplete = currentStep === 'record' && recordVerification;

    if (isWorkflowComplete) {
        return null;
    }

    return (
        <>
            <style>{`
                .vat-step-navigation {
                    background: #f8fafc;
                    padding: 1.5rem 2rem;
                    border-top: 1px solid #e5e7eb;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-radius: 0 0 12px 12px;
                }
                
                .vat-nav-button {
                    border-radius: 8px;
                    padding: 0.75rem 1.5rem;
                    font-weight: 600;
                    font-size: 0.875rem;
                    border: none;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    cursor: pointer;
                }
                
                .vat-btn-nav-secondary {
                    background: #f1f5f9;
                    color: #64748b;
                    border: 1px solid #e2e8f0;
                }
                
                .vat-btn-nav-secondary:hover:not(:disabled) {
                    background: #e2e8f0;
                    color: #475569;
                }
                
                .vat-btn-nav-primary {
                    background: #3b82f6;
                    color: white;
                }
                
                .vat-btn-nav-primary:hover:not(:disabled) {
                    background: #2563eb;
                }
                
                .vat-btn-nav-success {
                    background: #10b981;
                    color: white;
                }
                
                .vat-btn-nav-success:hover:not(:disabled) {
                    background: #059669;
                }
                
                .vat-nav-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                
                .vat-loading-spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid currentColor;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .vat-step-info {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.25rem;
                }
                
                .vat-step-number {
                    font-size: 0.875rem;
                    color: #64748b;
                    font-weight: 500;
                }
                
                .vat-step-progress {
                    font-size: 0.75rem;
                    color: #9ca3af;
                }
                
                /* Mobile responsive */
                @media (max-width: 768px) {
                    .vat-step-navigation {
                        flex-direction: column;
                        gap: 1rem;
                        align-items: stretch;
                        padding: 1rem;
                    }
                    
                    .vat-nav-button {
                        justify-content: center;
                    }
                    
                    .vat-step-info {
                        order: -1;
                        margin-bottom: 0.5rem;
                    }
                }
            `}</style>

            <div className="vat-step-navigation">
                <div>
                    {canGoBack && (
                        <button
                            className="vat-nav-button vat-btn-nav-secondary"
                            onClick={handlePrevious}
                            disabled={operationInProgress || vatPeriod?.state === 'closed'}
                        >
                            <ArrowLeft size={16} />
                            Tillbaka
                        </button>
                    )}
                </div>

                <div className="vat-step-info">
                    <div className="vat-step-number">
                        Steg {getCurrentStepIndex() + 1} av {stepOrder.length}
                    </div>
                    <div className="vat-step-progress">
                        {Math.round(((getCurrentStepIndex() + 1) / stepOrder.length) * 100)}% slutfört
                    </div>
                </div>

                <div>
                    {!isLastStep() ? (
                        <button
                            className="vat-nav-button vat-btn-nav-primary"
                            onClick={handleNext}
                            disabled={!canProceedToNext || operationInProgress || vatPeriod?.state === 'closed'}
                        >
                            {operationInProgress ? (
                                <>
                                    <div className="vat-loading-spinner" />
                                    Bearbetar...
                                </>
                            ) : (
                                <>
                                    {getNextButtonText()}
                                    <ArrowRight size={16} />
                                </>
                            )}
                        </button>
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default VatStepNavigation;